/**
 * DashboardActivationBanners — stackable post-launch activation prompts.
 *
 * Shown at the top of the dashboard for users who haven't connected
 * Google Calendar, Stripe, or their ICS feed. Each banner is:
 *  - Dismissible (X button)
 *  - Persistent dismissal via localStorage
 *  - Conditional (hidden when integration is already active)
 *
 * localStorage keys:
 *  - figgy_banner_dismissed_calendar
 *  - figgy_banner_dismissed_stripe
 *  - figgy_banner_dismissed_ics
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Calendar, CreditCard, Rss, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useGoogleCalendar } from '@/hooks/useGoogleCalendar';
import { useIcalFeed } from '@/hooks/useIcalFeed';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const DISMISSED_KEYS = {
  calendar: 'figgy_banner_dismissed_calendar',
  stripe:   'figgy_banner_dismissed_stripe',
  ics:      'figgy_banner_dismissed_ics',
} as const;

type BannerKey = keyof typeof DISMISSED_KEYS;

function readDismissed(): Set<BannerKey> {
  const out = new Set<BannerKey>();
  for (const [key, storageKey] of Object.entries(DISMISSED_KEYS)) {
    if (localStorage.getItem(storageKey) === '1') out.add(key as BannerKey);
  }
  return out;
}

// ─── Single Banner ────────────────────────────────────────────────────────────

interface BannerProps {
  icon: React.ReactNode;
  title: string;
  body: string;
  cta: string;
  ctaDisabled?: boolean;
  onCta: () => void;
  onDismiss: () => void;
}

function Banner({ icon, title, body, cta, ctaDisabled, onCta, onDismiss }: BannerProps) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm">
      <div className="shrink-0 text-indigo-500">{icon}</div>
      <div className="flex-1 min-w-0">
        <span className="font-semibold text-indigo-900">{title}. </span>
        <span className="text-indigo-700">{body}</span>
      </div>
      <button
        onClick={onCta}
        disabled={ctaDisabled}
        className="shrink-0 flex items-center gap-1 font-semibold text-indigo-600 hover:text-indigo-800 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
      >
        {cta}
        <ChevronRight className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        className="shrink-0 text-indigo-400 hover:text-indigo-600 ml-1"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function DashboardActivationBanners() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState<Set<BannerKey>>(() => readDismissed());
  // null = loading, false = not connected, true = connected
  const [stripeConnected, setStripeConnected] = useState<boolean | null>(null);

  const { integration: calIntegration, isLoading: calLoading } = useGoogleCalendar();
  const { feedUrl, loading: icsLoading } = useIcalFeed();

  // Check Stripe status — lightweight read from business_profiles.
  // stripe_account_id alone is not enough: the account must also have charges enabled
  // before clients can pay. Use limit(1)+order so duplicate rows don't cause an error.
  useEffect(() => {
    if (!user) return;
    supabase
      .from('business_profiles')
      .select('stripe_account_id, stripe_charges_enabled')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .then(({ data: rows }) => {
        const profile = rows?.[0] ?? null;
        setStripeConnected(
          profile ? (!!profile.stripe_account_id && !!profile.stripe_charges_enabled) : false
        );
      })
      .catch(() => {
        // Network error or RLS failure — hide the banner rather than leaving it in limbo.
        setStripeConnected(false);
      });
  }, [user]);

  function dismiss(key: BannerKey) {
    localStorage.setItem(DISMISSED_KEYS[key], '1');
    setDismissed(prev => new Set([...prev, key]));
  }

  async function copyIcalUrl() {
    if (!feedUrl) return;
    try {
      await navigator.clipboard.writeText(feedUrl);
      toast.success('Subscription link copied — paste it into any calendar app.');
    } catch {
      toast.error('Could not copy to clipboard. Find the link in Settings → Integrations.');
    }
  }

  const showCalendar = !calLoading && !calIntegration.isConnected && !dismissed.has('calendar');
  const showStripe   = stripeConnected === false && !dismissed.has('stripe');
  // Only show ICS banner once the feed URL is confirmed available — avoids showing
  // "Available in Settings" to users whose profile exists but token hasn't generated yet.
  const showIcs      = !icsLoading && !!feedUrl && !dismissed.has('ics');

  if (!showCalendar && !showStripe && !showIcs) return null;

  return (
    <div className="space-y-2">
      {showCalendar && (
        <Banner
          icon={<Calendar className="w-4 h-4" />}
          title="Avoid double bookings"
          body="Connect Google Calendar to check conflicts and auto-create events when clients book."
          cta="Connect Google Calendar"
          onCta={() => navigate('/dashboard/business-settings')}
          onDismiss={() => dismiss('calendar')}
        />
      )}
      {showStripe && (
        <Banner
          icon={<CreditCard className="w-4 h-4" />}
          title="Start accepting payments"
          body="Connect Stripe to collect card payments from clients on your booking page."
          cta="Set up payments"
          onCta={() => navigate('/dashboard/business-settings')}
          onDismiss={() => dismiss('stripe')}
        />
      )}
      {showIcs && (
        <Banner
          icon={<Rss className="w-4 h-4" />}
          title="Stay in sync"
          body="Add your booking calendar to Apple Calendar, Outlook, or any app — no sign-in required."
          cta={icsLoading ? 'Loading…' : feedUrl ? 'Copy subscription link' : 'Available in Settings'}
          ctaDisabled={icsLoading || !feedUrl}
          onCta={feedUrl ? copyIcalUrl : () => navigate('/dashboard/business-settings')}
          onDismiss={() => dismiss('ics')}
        />
      )}
    </div>
  );
}
